import React, { useEffect, useRef, useState } from "react";
import { Eye, MessageSquare, Mic } from "lucide-react";
import PersonaChat from "@/components/personas/PersonaChat";
import FocusGroupDialog from "@/components/personas/FocusGroupDialog";
import { base44 } from "@/api/base44Client";
import { useOrganisation } from "@/lib/OrganisationContext";

export default function Personas() {
  const { tenantRevision } = useOrganisation();
  const [personas, setPersonas] = useState([]);
  const [selected, setSelected] = useState(null);
  const [chatOpen, setChatOpen] = useState(false);
  const [mode, setMode] = useState("text");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [reloadToken, setReloadToken] = useState(0);
  const [focusGroupOpen, setFocusGroupOpen] = useState(false);
  const requestGeneration = useRef(0);

  useEffect(() => {
    const target = ++requestGeneration.current;
    const controller = new AbortController();
    setLoading(true);
    setError("");
    setSelected(null);
    setChatOpen(false);
    setFocusGroupOpen(false);
    base44.personas
      .list({ signal: controller.signal })
      .then((rows) => {
        if (requestGeneration.current === target) setPersonas(rows);
      })
      .catch((e) => {
        if (requestGeneration.current === target && e.name !== "AbortError")
          setError("Personas are temporarily unavailable.");
      })
      .finally(() => {
        if (requestGeneration.current === target) setLoading(false);
      });
    return () => {
      controller.abort();
      requestGeneration.current += 1;
    };
  }, [reloadToken, tenantRevision]);

  useEffect(() => {
    if (loading || !personas.length) return;
    const slug = new URLSearchParams(window.location.search).get("chatPersona");
    const target = slug && personas.find((persona) => persona.slug === slug);
    if (target) {
      setSelected(target);
      setMode("text");
      setChatOpen(true);
    }
  }, [loading, personas]);

  function openChat(persona, selectedMode) {
    setSelected(persona);
    setMode(selectedMode);
    setChatOpen(true);
  }

  return (
    <section
      className="personas-page mx-auto max-w-6xl p-8"
      aria-label="Personas"
    >
      <header className="mb-8">
        <div className="flex flex-wrap items-start justify-between gap-4"><div><h1 className="text-3xl font-bold text-[#0F1C2E]">Personas</h1>
        <p className="mt-1 max-w-2xl text-slate-500">
          AI personas built from real interviews with disabled people. Chat with
          them to understand lived experience and improve how your organisation
          serves disabled customers and colleagues.
        </p>
        </div><button type="button" disabled={loading || Boolean(error) || !personas.length} onClick={() => setFocusGroupOpen(true)} className="persona-primary min-h-11 rounded-xl bg-indigo-700 px-4 py-2 font-semibold text-white disabled:opacity-50">Create focus group</button></div>
      </header>
      {loading && (
        <p role="status" aria-live="polite">
          Loading active personas…
        </p>
      )}
      {error && (
        <div
          className="mb-4 flex flex-wrap items-center gap-3 text-sm text-red-700"
          role="alert"
        >
          <span>{error}</span>
          <button
            type="button"
            className="min-h-11 rounded-xl border border-red-700 px-4 py-2 font-semibold"
            onClick={() => setReloadToken((value) => value + 1)}
          >
            Retry
          </button>
        </div>
      )}
      {!loading && !error && (
        <>
          <div className="personas-grid grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {personas.map((persona) => (
              <article
                key={persona.id}
                className="persona-card flex flex-col rounded-2xl border border-slate-200 bg-white shadow-sm"
              >
                <div className="flex justify-center bg-gradient-to-br from-[#6366F1] to-[#818CF8] p-6">
                  {persona.avatar_url ? (
                    <img
                      data-decorative-image="true"
                      src={persona.avatar_url}
                      alt={`${persona.name} persona`}
                      className="h-28 w-28 rounded-full object-cover ring-4 ring-white/30"
                    />
                  ) : (
                    <div
                      data-decorative-image="true"
                      aria-hidden="true"
                      className="flex h-28 w-28 items-center justify-center rounded-full bg-indigo-600 text-3xl font-bold text-white ring-4 ring-white/30"
                    >
                      {(persona.name || persona.slug || "?")
                        .split(/\s+/)
                        .map((part) => part[0])
                        .join("")
                        .slice(0, 2)
                        .toUpperCase()}
                    </div>
                  )}
                </div>
                <div className="flex flex-1 flex-col p-6">
                  <div className="mb-1 flex flex-wrap items-center gap-2">
                    <h2 className="text-xl font-bold text-[#0F1C2E]">
                      {persona.name}
                    </h2>
                    <span className="inline-flex items-center gap-1 rounded-full bg-[#6366F1]/10 px-2 py-0.5 text-xs font-medium text-indigo-800">
                      <Eye className="h-3 w-3" aria-hidden="true" /> Lived
                      experience
                    </span>
                  </div>
                  <p className="mb-4 text-sm text-slate-600">
                    {persona.descriptor ||
                      "A lived experience persona available for an evidence grounded conversation."}
                  </p>
                  <div className="mt-auto grid grid-cols-2 gap-2">
                    <button
                      onClick={() => openChat(persona, "text")}
                      className="flex min-h-11 items-center justify-center gap-2 rounded-xl bg-[#6366F1] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#5558E0] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#6366F1] focus-visible:outline-offset-2"
                    >
                      <MessageSquare className="h-4 w-4" aria-hidden="true" />{" "}
                      Text Chat
                    </button>
                    <button
                      onClick={() => openChat(persona, "voice")}
                      className="flex min-h-11 items-center justify-center gap-2 rounded-xl border border-[#6366F1] bg-white px-4 py-2.5 text-sm font-semibold text-indigo-800 hover:bg-[#6366F1]/5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#6366F1] focus-visible:outline-offset-2"
                    >
                      <Mic className="h-4 w-4" aria-hidden="true" /> Microphone
                      Chat
                    </button>
                  </div>
                </div>
              </article>
            ))}
          </div>
          {!personas.length && (
            <p className="text-sm text-slate-500">
              No active personas are available.
            </p>
          )}
        </>
      )}
      {selected && (
        <PersonaChat
          key={`${selected.id}-${tenantRevision}`}
          persona={selected}
          open={chatOpen}
          onClose={() => setChatOpen(false)}
          initialMode={mode}
        />
      )}
      <FocusGroupDialog open={focusGroupOpen} onClose={() => setFocusGroupOpen(false)} personas={personas} tenantRevision={tenantRevision} />
    </section>
  );
}
